import { useLingui } from '@lingui/react/macro';
import { Input } from '@utima/ui-informed';
import { useFormState } from 'informed';
import { useCallback } from 'react';

import { Card } from '@/components/card/Card';

import { PasswordHint } from './PasswordHint';
import {
  MIN_LENGTH,
  passwordValidate,
  PasswordValidationError,
} from './passwordValidate';

export type PasswordFieldsProps = {
  hasOldPassword?: boolean;
  title?: string;
};

export function PasswordFields({
  hasOldPassword = true,
  title,
}: PasswordFieldsProps) {
  const { t } = useLingui();
  const { values } = useFormState();

  const onValidate = useCallback(
    (value: unknown, values: Record<string, unknown>) => {
      const errors = passwordValidate(
        values.password as string,
        values.confirmPassword as string,
      );

      if (errors?.includes(PasswordValidationError.LENGTH)) {
        return t`Password must be at least ${MIN_LENGTH} characters long`;
      }

      if (errors?.includes(PasswordValidationError.MATCH)) {
        return t`Passwords do not match`;
      }

      if (errors?.includes(PasswordValidationError.NUMBER)) {
        return t`Password must contain at least one number`;
      }

      if (errors?.includes(PasswordValidationError.SPECIAL)) {
        return t`Password must contain at least one special character`;
      }

      if (errors?.includes(PasswordValidationError.UPPERCASE)) {
        return t`Password must contain at least one uppercase letter`;
      }
    },
    [t],
  );

  const passwordFieldsRequired = !!(
    (hasOldPassword && values.oldPassword) ||
    values.password ||
    values.confirmPassword
  );

  return (
    <Card title={title ?? t`Password change`}>
      {hasOldPassword && (
        <Input
          type='password'
          required={passwordFieldsRequired}
          name='oldPassword'
          label={t`Password`}
          showOptional={false}
          passwordPreview
          showErrorIfError
        />
      )}
      <Input
        type='password'
        required={passwordFieldsRequired}
        name='password'
        label={t`New password`}
        validate={onValidate}
        validateOn='change'
        showOptional={false}
        passwordPreview
        showErrorIfError
        validateWhen={['confirmPassword']}
      />
      <Input
        type='password'
        required={passwordFieldsRequired}
        name='confirmPassword'
        label={t`Confirm password`}
        validate={onValidate}
        validateOn='change'
        showOptional={false}
        passwordPreview
        showErrorIfError
        validateWhen={['password']}
      />
      <PasswordHint />
    </Card>
  );
}
